import { useEffect, useRef } from "react";
import { isPerformanceApiAvailable, measure } from "../utils";

/**
 * Measures the time a condition met. This is useful for measuring how long an
 * effect takes to run. Does nothing if the Performance API is not available.
 *
 * Example usage:
 * ```ts
 * const { loading, data } = useSearchExample(text);
 * useClock('search-example', loading);
 * ```
 */
export function useClock(
  name: string,
  isRunning: boolean,
  detail?: Record<keyof any, unknown>
) {
  const isMeasuring = useRef(false);

  useEffect(() => {
    if (!isPerformanceApiAvailable()) {
      return;
    }

    const markName = `${name}_start`;

    // Mark the start of the measurement
    if (isRunning) {
      window.performance.mark(markName);
      isMeasuring.current = true;
      return;
    }

    const hasMark =
      window.performance.getEntriesByName(markName, "mark").length > 0;

    // Measure the duration of the measurement
    if (isMeasuring.current && hasMark) {
      const measurement = measure(name, {
        detail,
        start: markName,
      });

      isMeasuring.current = false;

      console.debug(`⏱️ ${name} measured ${measurement.duration}ms`);
    }
  }, [name, isRunning]);
}
